import {
  forwardRef,
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Icons } from './icons.js';

/**
 * Shared React building blocks of Oppy (docs/design/README.md). Styling
 * lives in styles.css (class names prefixed with `ui-`); components avoid
 * inline styles so the page can run under a strict Content-Security-Policy.
 */

const cx = (...names: (string | false | null | undefined)[]) => names.filter(Boolean).join(' ');

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  busy?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** a leading icon */
  icon?: ReactNode;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', busy = false, size = 'md', icon, block = false, className, disabled, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx('ui-button', `ui-button--${variant}`, size !== 'md' && `ui-button--${size}`, block && 'ui-button--block', className)}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <span className="ui-spinner ui-spinner--inline" aria-hidden="true" /> : icon}
      {children}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** the accessible name, also shown as a tooltip */
  label: string;
  icon: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'ghost' | 'primary' | 'outlined';
  busy?: boolean;
}

/** A round button with only an icon; `label` names it for screen readers and as a tooltip. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, size = 'md', variant = 'ghost', busy = false, className, disabled, type = 'button', title, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={title ?? label}
      className={cx('ui-icon-button', size !== 'md' && `ui-icon-button--${size}`, variant !== 'ghost' && `ui-icon-button--${variant}`, className)}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <span className="ui-spinner ui-spinner--inline" aria-hidden="true" /> : icon}
    </button>
  );
});

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

/** Label + control + hint/error with the right aria wiring. */
export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="ui-field">
      <label className="ui-field__label" htmlFor={id}>
        {label}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {hint ? (
        <p id={hintId} className="ui-field__hint">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="ui-field__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function TextInput({ className, ...rest }, ref) {
  return <input ref={ref} className={cx('ui-input', className)} {...rest} />;
});

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function TextArea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cx('ui-input', 'ui-textarea', className)} {...rest} />;
});

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: 'sm' | 'md';
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select({ className, children, size = 'md', ...rest }, ref) {
  return (
    <select ref={ref} className={cx('ui-input', 'ui-select', size === 'sm' && 'ui-select--sm', className)} {...rest}>
      {children}
    </select>
  );
});

/** A checkbox drawn as a toggle (green when on). Give it a label or aria-label. */
export const Switch = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>>(function Switch({ className, ...rest }, ref) {
  return <input ref={ref} type="checkbox" role="switch" className={cx('ui-switch', className)} {...rest} />;
});

export function Card({
  title,
  actions,
  children,
  className,
  ...rest
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}) {
  return (
    <section className={cx('ui-card', className)} {...rest}>
      {title || actions ? (
        <header className="ui-card__header">
          {title ? <h2 className="ui-card__title">{title}</h2> : <span />}
          {actions ? <div className="ui-card__actions">{actions}</div> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export type AlertTone = 'error' | 'info' | 'success' | 'warning';

const ALERT_ICONS = { error: Icons.error, info: Icons.info, success: Icons.done, warning: Icons.warning } as const;

/**
 * Message box. Technical details stay folded behind `detailsLabel` (D-39):
 * the plain-language text comes first, the code and request id on demand.
 */
export function Alert({
  tone = 'info',
  title,
  children,
  details,
  detailsLabel,
  action,
  ...rest
}: {
  tone?: AlertTone;
  title: ReactNode;
  children?: ReactNode;
  details?: ReactNode;
  detailsLabel?: ReactNode;
  action?: ReactNode;
  'data-testid'?: string;
}) {
  const Icon = ALERT_ICONS[tone];
  return (
    <div className={`ui-alert ui-alert--${tone}`} role={tone === 'error' ? 'alert' : 'status'} {...rest}>
      <Icon />
      <div className="ui-alert__content">
        <p className="ui-alert__title">{title}</p>
        {children ? <div className="ui-alert__body">{children}</div> : null}
        {action ? <div className="ui-alert__action">{action}</div> : null}
        {details ? (
          <details className="ui-alert__details">
            <summary>{detailsLabel ?? 'Details'}</summary>
            <div className="ui-alert__details-body">{details}</div>
          </details>
        ) : null}
      </div>
    </div>
  );
}

export type BadgeTone = 'neutral' | 'accent' | 'info' | 'success' | 'warning' | 'danger';

export function Badge({ children, tone = 'neutral', className }: { children: ReactNode; tone?: BadgeTone; className?: string }) {
  return <span className={cx('ui-badge', `ui-badge--${tone}`, className)}>{children}</span>;
}

export interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  icon?: ReactNode;
}

/** A pill-shaped toggle or filter; `active` sets aria-pressed. */
export function Chip({ active, icon, className, children, type = 'button', ...rest }: ChipProps) {
  return (
    <button type={type} className={cx('ui-chip', className)} aria-pressed={active === undefined ? undefined : active} {...rest}>
      {icon}
      {children}
    </button>
  );
}

export function Dot({ tone, className }: { tone?: 'chat' | 'code' | 'success' | 'running' | 'warning' | 'danger'; className?: string }) {
  return <span aria-hidden="true" className={cx('ui-dot', tone && `ui-dot--${tone}`, className)} />;
}

export function Spinner({ label }: { label: string }) {
  return (
    <span className="ui-spinner-wrap" role="status">
      <span className="ui-spinner" aria-hidden="true" />
      <span className="ui-visually-hidden">{label}</span>
    </span>
  );
}

/** A page whose data is on its way: a centred spinner, announced to screen readers. */
export function PageLoading({ label }: { label: string }) {
  return (
    <div className="ui-page-loading" data-testid="page-loading">
      <Spinner label={label} />
    </div>
  );
}

/** Placeholder lines while a list or card loads. */
export function Skeleton({ lines = 3, block = false }: { lines?: number; block?: boolean }) {
  if (block) return <span className="ui-skeleton ui-skeleton--block" aria-hidden="true" />;
  return (
    <span aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <span key={i} className={cx('ui-skeleton', i === lines - 1 && lines > 1 && 'ui-skeleton--medium')} />
      ))}
    </span>
  );
}

/**
 * Nothing to show yet. With only children it is a quiet line; with a title
 * it becomes a centred block with an icon and an optional action.
 */
export function EmptyState({
  children,
  title,
  icon,
  action,
  ...rest
}: {
  children?: ReactNode;
  title?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  'data-testid'?: string;
}) {
  if (!title) return <p className="ui-empty" {...rest}>{children}</p>;
  return (
    <div className="ui-empty-state" {...rest}>
      {icon ? <span className="ui-empty-state__icon">{icon}</span> : null}
      <p className="ui-empty-state__title">{title}</p>
      {children ? <p className="ui-empty-state__body">{children}</p> : null}
      {action ? <div className="ui-empty-state__action">{action}</div> : null}
    </div>
  );
}

/** A bar for a share of a limit; green, amber near the limit, red past it. */
export function Progress({ value, max, label, tone }: { value: number; max: number; label: string; tone?: 'success' | 'warning' | 'danger' | 'running' }) {
  const ratio = max > 0 ? value / max : 0;
  const shown = tone ?? (ratio >= 1 ? 'danger' : ratio >= 0.8 ? 'warning' : 'success');
  return <progress className={cx('ui-progress', shown !== 'success' && `ui-progress--${shown}`)} value={Math.min(value, max)} max={max} aria-label={label} />;
}

/** A region that scrolls sideways (a wide table): it takes keyboard focus so it scrolls without a mouse (WCAG 2.1.1). */
export function ScrollArea({ label, children, className, ...rest }: { label: string; children: ReactNode; className?: string; 'data-testid'?: string }) {
  return (
    <div className={cx('ui-scroll-area', className)} role="region" aria-label={label} tabIndex={0} {...rest}>
      {children}
    </div>
  );
}

export const initialsOf = (name: string) =>
  name
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toLocaleUpperCase('tr'))
    .join('') || '?';

/** One of four tints, the same for a name every time (avatars in a row tell people apart). */
export const toneOf = (key: string): 'blue' | 'green' | 'pink' | 'amber' => {
  let hash = 0;
  for (const char of key) hash = (hash * 31 + (char.codePointAt(0) ?? 0)) >>> 0;
  return (['blue', 'green', 'pink', 'amber'] as const)[hash % 4] ?? 'blue';
};

export function Avatar({ name, tone, size = 'md', className }: { name: string; tone?: 'blue' | 'green' | 'pink' | 'amber'; size?: 'sm' | 'md' | 'lg'; className?: string }) {
  return (
    <span className={cx('ui-avatar', tone && `ui-avatar--${tone}`, size !== 'md' && `ui-avatar--${size}`, className)} aria-hidden="true">
      {initialsOf(name)}
    </span>
  );
}

/** Members as overlapping initials; the group is named by `label` ("3 üye"). */
export function AvatarStack({ people, label, max = 4, size = 'sm' }: { people: { id: string; name: string }[]; label: string; max?: number; size?: 'sm' | 'md' }) {
  return (
    <span className="ui-avatar-stack" role="img" aria-label={label} title={people.map((p) => p.name).join(', ')}>
      {people.slice(0, max).map((person) => (
        <Avatar key={person.id} name={person.name} tone={toneOf(person.id)} size={size} />
      ))}
    </span>
  );
}

/** A choice drawn as a card: the radio (or, with `multiple`, a check box), a title and a line under it (who can use a skill, an answer option). */
export const RadioCard = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'title' | 'multiple'> & { title: ReactNode; hint?: ReactNode; multiple?: boolean; 'data-testid'?: string }
>(function RadioCard({ title, hint, multiple = false, className, 'data-testid': testId, ...rest }, ref) {
  return (
    <label className={cx('ui-radio-card', className)} data-testid={testId}>
      <input ref={ref} type={multiple ? 'checkbox' : 'radio'} className="ui-radio" {...rest} />
      <span className="ui-radio-card__text">
        <span className="ui-radio-card__title">{title}</span>
        {hint ? <span className="ui-radio-card__hint">{hint}</span> : null}
      </span>
    </label>
  );
});
